class DrawerSearchSection extends HTMLElement {
  constructor() {
    super();

    this.isOpen = false;
    this.drawerClass = "wt-drawer-search";
    this.classDrawerActive = `${this.drawerClass}--active`;
    this.pageOverlayClass = "search-overlay";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;

    // Use ID to scope the input correctly
    this.input = this.querySelector(`#search-shop-${this.id}`);
    this.clearButton = this.querySelector(".wt-header__search__clear-button");
    this.closeButton = this.querySelector(".wt-header__search__close");
    this.mainTrigger = this.querySelector(".wt-header__search-trigger");
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.statusElement = this.querySelector(".predictive-search-status");
    this.loadingText = this.getAttribute("data-loading-text");

    this.toggleTabindexElements = [this.input, this.closeButton];
    this.isVisibleClearButton = false;
    this.cachedResults = {};

    this.init();
    this.setupEventListeners();
  }

  getFocusableElements() {
    const focusableElements = () =>
      Array.from(this.querySelectorAll("button, [href], input:not([type='hidden']), select, [tabindex]"))
        .filter((el) => !el.hasAttribute("disabled") && el.tabIndex >= 0);

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.setTabindex(this.toggleTabindexElements, "-1");
      this.setTabindex([this.mainTrigger], "0");
      this.classList.remove(this.classDrawerActive);
      this.isOpen = false;

      setTimeout(() => this.mainTrigger?.focus(), 0);
    } else {
      // Close other drawers
      document.querySelectorAll("search-drawer").forEach((drawer) => {
        if (drawer !== this) {
          drawer.removeAttribute("open");
          drawer.classList.remove(drawer.classDrawerActive);
        }
      });

      this.setAttribute("open", "");
      this.setTabindex(this.toggleTabindexElements, "0");
      this.setTabindex([this.mainTrigger], "-1");
      this.classList.add(this.classDrawerActive);
      this.isOpen = true;
      this.input?.focus();
    }

    this.toggleBodyOverlay();
  }

  toggleBodyOverlay() {
    const anyOpen = document.querySelectorAll(`.${this.classDrawerActive}`).length > 0;
    if (this.id != "mega-menu") {
      this.body.classList.toggle(this.activeOverlayBodyClass, anyOpen);
    }
  }

  setTabindex(elements, value) {
    elements.forEach((el) => el?.setAttribute("tabindex", value));
  }

  toggleDrawerClasses() {
    this.onToggle();
  }

  init() {
    this.mainTrigger?.addEventListener("click", (e) => {
      e.preventDefault();
      this.toggleDrawerClasses();
    });

    this.closeButton?.addEventListener("click", (e) => {
      e.preventDefault();
      this.toggleDrawerClasses();
    });

    this.clearButton?.addEventListener("click", () => {
      this.input.value = "";
      this.clearButton.style.display = "none";
      this.isVisibleClearButton = false;
      this.clearResults();
    });

    if (this.input) {
      this.input.addEventListener("input", () => {
        const hasValue = this.input.value.length > 0;
        this.clearButton.style.display = hasValue ? "flex" : "none";
        this.clearButton.setAttribute("tabindex", hasValue ? "0" : "-1");
        this.isVisibleClearButton = hasValue;
        if (!hasValue) {
          this.querySelector(".wt-header__search__results")?.classList.remove("search-performed");
        }
      });

      this.input.addEventListener("input", debounce((event) => {
        this.onChange(event);
      }, 300).bind(this));
    }

    this.addEventListener("keydown", (e) => {
      const { first, last } = this.getFocusableElements();
      const isTab = e.key === "Tab" || e.keyCode === 9;
      const isEsc = e.key === "Escape" || e.keyCode === 27;

      if (isEsc && this.isOpen) {
        this.toggleDrawerClasses();
      }

      if (isTab && this.isOpen) {
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    });
  }

  setupEventListeners() {
    this.querySelector(`form.store-search-form-${this.id}`)?.addEventListener("submit", this.onFormSubmit.bind(this));
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  getQuery() {
    return this.input?.value.trim() || "";
  }

  onChange() {
    const term = this.getQuery();
    if (term.length) {
      this.getSearchResults(term);
      this.removeAttribute("empty");
    } else {
      this.clearResults();
    }
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent("resources[type]")}=product,page,article,collection,query&${encodeURIComponent("resources[limit_scope]")}=each&${encodeURIComponent("resources[limit]")}=6&${encodeURIComponent("resources[options][fields]")}=title,variants.sku,product_type&section_id=predictive-search`
    )
      .then((res) => res.ok ? res.text() : Promise.reject(res.status))
      .then((text) => {
        const html = new DOMParser().parseFromString(text, "text/html");
        const resultsMarkup = html.querySelector("#shopify-section-predictive-search")?.innerHTML || "";
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
        this.querySelector(".wt-header__search__results")?.classList.add("search-performed");
      })
      .catch((err) => console.error("Search error:", err));
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);
    this.setLiveRegionResults();
  }

  clearResults() {
    this.input.value = "";
    this.removeAttribute("results");
    this.setAttribute("empty", true);
  }

  setLiveRegionLoadingState() {
    if (this.statusElement && this.loadingText) {
      this.setLiveRegionText(this.loadingText);
      this.setAttribute("loading", true);
    }
  }

  setLiveRegionText(text) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = text;
    setTimeout(() => this.statusElement.setAttribute("aria-hidden", "true"), 1000);
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    const count = this.querySelector("[data-predictive-search-live-region-count-value]")?.textContent;
    if (count) this.setLiveRegionText(count);
  }
}

customElements.define("search-drawer", DrawerSearchSection);
